import { Check, Clock, User, ArrowRight, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDateShort } from "@/utils/dateFormat";

/**
 * SigningWorkflow - Visual workflow diagram for envelope signing progress
 *
 * Shows recipients in order with status indicators and connecting lines
 */
export const SigningWorkflow = ({ envelope, user, className }) => {
  const { t } = useTranslation();

  if (!envelope?.recipients || envelope.recipients.length === 0) {
    return null;
  }

  const isSequential = envelope.signingOrder === "sequential";

  // Sort recipients by orderIndex for sequential, or keep original order for parallel
  const sortedRecipients = [...envelope.recipients].sort((a, b) => {
    if (isSequential) {
      return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
    }
    return 0;
  });

  // Determine current signer index for sequential signing
  const getCurrentSignerIndex = () => {
    if (!isSequential) return -1;

    for (let i = 0; i < sortedRecipients.length; i++) {
      const r = sortedRecipients[i];
      if (r.status !== "signed" && r.status !== "declined") {
        return i;
      }
    }
    return sortedRecipients.length; // All done
  };

  const currentSignerIndex = getCurrentSignerIndex();

  // Get status info for a recipient
  const getRecipientStatus = (recipient, index) => {
    if (recipient.status === "signed") {
      return {
        type: "completed",
        icon: Check,
        color: "bg-green-500 text-white",
        ringColor: "ring-green-500",
        lineColor: "bg-green-500",
        label: t("signing.workflow.signed", "Signed"),
        timestamp: recipient.signedAt,
      };
    }

    if (recipient.status === "declined") {
      return {
        type: "declined",
        icon: User,
        color: "bg-red-500 text-white",
        ringColor: "ring-red-500",
        lineColor: "bg-red-300",
        label: t("signing.workflow.declined", "Declined"),
        timestamp: recipient.declinedAt,
      };
    }

    if (isSequential && index === currentSignerIndex) {
      return {
        type: "current",
        icon: Clock,
        color: "bg-blue-500 text-white",
        ringColor: "ring-blue-500",
        lineColor: "bg-gray-300",
        label: t("signing.workflow.current", "Awaiting signature"),
        timestamp: null,
      };
    }

    if (
      !isSequential &&
      ["sent", "delivered", "viewed"].includes(recipient.status)
    ) {
      return {
        type: "active",
        icon: Clock,
        color: "bg-blue-500 text-white",
        ringColor: "ring-blue-500",
        lineColor: "bg-gray-300",
        label: t("signing.workflow.pending", "Pending"),
        timestamp: null,
      };
    }

    return {
      type: "pending",
      icon: User,
      color: "bg-gray-200 text-gray-500",
      ringColor: "ring-gray-300",
      lineColor: "bg-gray-200",
      label: t("signing.workflow.waiting", "Waiting"),
      timestamp: null,
    };
  };

  // Parallel view - all recipients in a grid
  if (!isSequential) {
    return (
      <div className={cn("", className)}>
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {t(
              "signing.workflow.parallel",
              "All recipients can sign simultaneously",
            )}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {sortedRecipients.map((recipient) => {
            const status = getRecipientStatus(recipient, 0);
            const StatusIcon = status.icon;

            return (
              <TooltipProvider key={recipient.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border transition-all dark:bg-background",
                        status.type === "completed" &&
                          "border-green-200 bg-green-50",
                        status.type === "declined" &&
                          "border-red-200 bg-red-50",
                        status.type === "active" &&
                          "border-blue-200 bg-blue-50",
                        status.type === "pending" &&
                          "border-gray-200 bg-gray-50",
                      )}
                    >
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          status.color,
                        )}
                      >
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {recipient.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {status.label}
                        </p>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{recipient.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {recipient.email}
                    </p>
                    {status.timestamp && (
                      <p className="text-xs mt-1">
                        {status.label}:{" "}
                        {formatDateShort(status.timestamp, user)}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </div>
    );
  }

  // Sequential view - horizontal timeline
  return (
    <div className={cn("", className)}>
      <div className="flex items-center gap-2 mb-4">
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">
          {t("signing.workflow.sequential", "Recipients sign in order")}
        </span>
      </div>

      {/* Desktop: Horizontal timeline */}
      <div className="hidden sm:flex items-start">
        {sortedRecipients.map((recipient, index) => {
          const status = getRecipientStatus(recipient, index);
          const StatusIcon = status.icon;
          const isLast = index === sortedRecipients.length - 1;

          return (
            <div key={recipient.id} className="flex items-start flex-1">
              {/* Node */}
              <div className="flex flex-col items-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center ring-2 ring-offset-2 cursor-default transition-all",
                          status.color,
                          status.ringColor,
                        )}
                      >
                        <StatusIcon className="w-5 h-5" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{recipient.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {recipient.email}
                      </p>
                      {status.timestamp && (
                        <p className="text-xs mt-1">
                          {status.label}:{" "}
                          {formatDateShort(status.timestamp, user)}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Step number */}
                <span className="text-xs font-medium text-muted-foreground mt-1">
                  {index + 1}
                </span>

                {/* Recipient info */}
                <div className="mt-2 text-center max-w-24">
                  <p className="text-sm font-medium truncate">
                    {recipient.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {status.label}
                  </p>
                </div>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 flex items-center px-2 mt-5">
                  <div
                    className={cn(
                      "h-0.5 w-full rounded transition-colors",
                      status.type === "completed"
                        ? "bg-green-500"
                        : "bg-gray-200",
                    )}
                  />
                  <ArrowRight
                    className={cn(
                      "w-4 h-4 -ml-1 shrink-0",
                      status.type === "completed"
                        ? "text-green-500"
                        : "text-gray-300",
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: Vertical timeline */}
      <div className="sm:hidden space-y-0">
        {sortedRecipients.map((recipient, index) => {
          const status = getRecipientStatus(recipient, index);
          const StatusIcon = status.icon;
          const isLast = index === sortedRecipients.length - 1;

          return (
            <div key={recipient.id} className="flex">
              {/* Timeline column */}
              <div className="flex flex-col items-center mr-4">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center ring-2 ring-offset-2 shrink-0",
                    status.color,
                    status.ringColor,
                  )}
                >
                  <StatusIcon className="w-5 h-5" />
                </div>
                {!isLast && (
                  <div
                    className={cn(
                      "w-0.5 flex-1 my-2",
                      status.type === "completed"
                        ? "bg-green-500"
                        : "bg-gray-200",
                    )}
                  />
                )}
              </div>

              {/* Content column */}
              <div className={cn("pb-6 flex-1", isLast && "pb-0")}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {t("signing.workflow.step", "Step")} {index + 1}
                  </span>
                </div>
                <p className="font-medium mt-1">{recipient.name}</p>
                <p className="text-sm text-muted-foreground">
                  {recipient.email}
                </p>
                <p
                  className={cn(
                    "text-sm mt-1",
                    status.type === "completed" && "text-green-600",
                    status.type === "declined" && "text-red-600",
                    status.type === "current" && "text-blue-600",
                    status.type === "pending" && "text-gray-500",
                  )}
                >
                  {status.label}
                  {status.timestamp &&
                    ` • ${formatDateShort(status.timestamp, user)}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SigningWorkflow;
